import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import logo from "../../assets/logoProd.png";
import logoLight from "../../assets/logoProdLight.png";
import { useIsMobile } from "../../App";

const EmailVerificationScreen = () => {
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [resendCooldown, setResendCooldown] = useState(0);
  const [email, setEmail] = useState("");
  const timerRef = useRef(null);

  const { verifyEmail, resendVerificationCode } = useAuth();
  const { theme } = useTheme();
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem("pendingVerificationEmail");
    const cooldownFromStorage = localStorage.getItem("resendCooldown");
    
    if (emailFromState) {
      setEmail(emailFromState);
      localStorage.setItem("pendingVerificationEmail", emailFromState);
      localStorage.removeItem("resendCooldown");
      localStorage.removeItem("resendTimestamp");
      localStorage.removeItem("hasSentCode");
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      navigate("/register");
    }

    if (cooldownFromStorage && !emailFromState) {
      const savedCooldown = parseInt(cooldownFromStorage);
      const savedTimestamp = localStorage.getItem("resendTimestamp");
      if (savedTimestamp) {
        const timePassed = Math.floor((Date.now() - parseInt(savedTimestamp)) / 1000);
        const remainingCooldown = Math.max(0, savedCooldown - timePassed);
        setResendCooldown(remainingCooldown);
      }
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (email && resendCooldown === 0) {
      const hasSentCode = localStorage.getItem("hasSentCode");
      if (!hasSentCode) {
        handleResendCode(true);
        localStorage.setItem("hasSentCode", "true");
      }
    }
  }, [email]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else {
      localStorage.removeItem("resendCooldown");
      localStorage.removeItem("resendTimestamp");
    }
  }, [resendCooldown]);

  const handleInputChange = (value) => {
    setVerificationCode(value);
    if (errors.code) {
      setErrors((prev) => ({
        ...prev,
        code: undefined,
      }));
    }
  };

  const validateCode = () => {
    if (!verificationCode.trim()) {
      setErrors({ code: t("verificationCodeRequired") || "Код підтвердження обов'язковий" });
      return false;
    }
    if (verificationCode.trim().length !== 6) {
      setErrors({ code: t("verificationCodeLength") || "Код повинен містити 6 цифр" });
      return false;
    }
    return true;
  };

  const handleVerifyCode = async () => {
    if (!validateCode()) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await verifyEmail(email, verificationCode.trim());
      
      if (result.success) {
        localStorage.removeItem("pendingVerificationEmail");
        localStorage.removeItem("resendCooldown");
        localStorage.removeItem("resendTimestamp");
        localStorage.removeItem("hasSentCode");
        localStorage.setItem("show_welcome_after_register", "1");
        navigate("/chat/new");
      } else {
        setErrors({ 
          success: result.error || t("verificationFailed") || "Помилка підтвердження коду" 
        });
      }
    } catch (error) {
      setErrors({ 
        success: t("verificationFailed") || "Помилка підтвердження коду" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (isAutoSend = false) => {
    if (resendCooldown > 0 && !isAutoSend) return;

    setLoading(true);
    setErrors({});

    try {
      const result = await resendVerificationCode(email);
      
      if (result.success) {
        if (!isAutoSend) {
          setResendCooldown(120);
          localStorage.setItem("resendCooldown", "120");
          localStorage.setItem("resendTimestamp", Date.now().toString());
        }
        setErrors({ 
          success: t("codeResent") || "Код повторно відправлено на вашу пошту" 
        });
      } else {
        setErrors({ 
          success: result.error || t("resendFailed") || "Помилка відправки коду" 
        });
      }
    } catch (error) {
      setErrors({ 
        success: t("resendFailed") || "Помилка відправки коду" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVerifyCode();
    }
  };

  const handleBackToRegister = () => {
    localStorage.removeItem("pendingVerificationEmail");
    localStorage.removeItem("resendCooldown");
    localStorage.removeItem("resendTimestamp");
    localStorage.removeItem("hasSentCode");
    navigate("/register");
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: theme.background,
    padding: isMobile ? "8px 0" : "32px 0",
    boxSizing: "border-box",
  };

  const formStyle = {
    width: isMobile ? "96vw" : 400,
    maxWidth: 400,
    background: theme.surface,
    borderRadius: isMobile ? 8 : 16,
    boxShadow: theme.isDark ? "0 2px 8px #0005" : "0 2px 8px #0002",
    padding: isMobile ? "16px 8px" : "32px",
    boxSizing: "border-box",
  };

  const titleStyles = {
    fontSize: "26px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: "16px",
    color: theme.text,
  };

  const subtitleStyles = {
    fontSize: "16px",
    textAlign: "center",
    marginBottom: "32px",
    color: theme.textSecondary || theme.text,
    opacity: 0.8,
  };

  const formStyles = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginBottom: "24px",
    alignItems: "center",
  };

  const linkStyles = {
    textAlign: "center",
    color: theme.primary,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "16px",
    width: "100%",
  };

  const errorStyles = {
    color: theme.error,
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const successStyles = {
    color: theme.success || "#4caf50",
    fontSize: "14px",
    textAlign: "center",
    marginBottom: "16px",
  };

  const resendStyles = {
    textAlign: "center",
    color: resendCooldown > 0 ? theme.textSecondary || theme.text : theme.primary,
    cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
    textDecoration: "none",
    fontSize: "14px",
    marginTop: "8px",
    opacity: resendCooldown > 0 ? 0.6 : 1,
    width: "100%",
  };

  const logoStyles = {
    display: "block",
    margin: "0 auto 24px auto",
    width: "250px",
    height: "auto",
  };

  const isDark = theme.isDark;
  const logoToShow = isDark ? logoLight : logo;

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <img src={logoToShow} alt="Logo" style={logoStyles} />
        <h1 style={titleStyles}>{t("emailVerification") || "Підтвердження Email"}</h1>
        
        <p style={subtitleStyles}>
          {t("verificationEmailSent") || "Ми відправили код підтвердження на"}{" "}
          <strong>{email}</strong>
        </p>

        {errors.success && <div style={successStyles}>{errors.success}</div>}

        <div style={formStyles}>
          <Input
            label={t("verificationCode") || "Код підтвердження"}
            type="text"
            placeholder="123456"
            value={verificationCode}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            error={errors.code}
            required
            autoComplete="off"
            maxLength="6"
          />

          <Button
            className="verify-button"
            onClick={handleVerifyCode}
            loading={loading}
            disabled={!verificationCode.trim() || verificationCode.trim().length !== 6}
            size={isMobile ? "small" : "medium"}
            style={{ 
              whiteSpace: "nowrap",
              width: "auto",
              minWidth: "200px",
              padding: "12px 32px"
            }}
          >
            {t("verifyEmail") || "Підтвердити Email"}
          </Button>

          <div style={resendStyles} onClick={handleResendCode}>
            {resendCooldown > 0 
              ? `${t("resendCodeIn") || "Повторно відправити через"} ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}`
              : t("resendCode") || "Відправити код повторно"
            }
          </div>
        </div>

        <div style={linkStyles} onClick={handleBackToRegister}>
          ← {t("backToRegister") || "Повернутися до реєстрації"}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;
